// FileViewer.jsx
// Context used by the FileViewer components to share state and functions.

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt, Valentin Haas

// Framework imports
import React from "react";
import PropTypes from "prop-types";
import { useHistory, useParams } from "react-router-dom";

// Material UI imports
import { Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";

// HSA KIT Components
import Backend from "../common/utils/Backend";
import FileViewerSideBar from "./components/FileViewerSideBar";
import FileViewerSplitscreenManager from "./components/FileViewerSplitscreenManager";
import FileFormatUtils from "../common/utils/FileFormatUtils";

// HSA Imports
import { withPersistentStorage } from "../viewer/contexts/PersistentStorageContext";
import { withTiles } from "../viewer/contexts/TilesContext";
import CommentTool from "../viewer/components/tools/CommentTool";
import EventBus from "../common/services/EventBus";
import ViewHeatMapTool from "../viewer/components/tools/ViewHeatMapTool";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    height: "100%",
    background: "#EBEBEB",
    overflow: "hidden",
    position: "relative",
    display: "grid",
    gridTemplateColumns: "1fr auto;",
  },
}));

export const FileViewerContext = React.createContext();

/**
 * Enum-like object for file sorting types.
 * @readonly
 * @enum {string}
 * @property {string} Name - Sort by file name.
 * @property {string} Date - Sort by file creation date.
 * @property {string} ChangeDate - Sort by file modification date.
 * @property {string} Type - Sort by file ending type (e.g., .tif, .png).
 */
export const SortByType = Object.freeze({
  Name: "name",
  Date: "date",
  ChangeDate: "changeDate",
  Type: "type",
});

/**
 * Enum-like object representing the available tools in the File Viewer.
 * @readonly
 * @enum {string}
 * @property {string} Comment - Tool for adding comments.
 * @property {string} Stream - Tool for streaming Renderer canvas.
 * @property {string} None - No tool selected.
 */
export const FileViewerTools = Object.freeze({
  Comment: "comment",
  ViewHeatMap: "ViewHeatMap",
  Stream: "stream",
  None: "none",
});

function FileViewer(props) {
  const tools = React.useMemo(
    () => ({
      [FileViewerTools.Comment]: new CommentTool(),
      [FileViewerTools.ViewHeatMap]: new ViewHeatMapTool(),
      [FileViewerTools.None]: null,
    }),
    []
  );
  const classes = useStyles();
  const history = useHistory();
  const { caseUrlId } = useParams();

  const rendererDictRef = React.useRef({});

  //case to fileId handling
  const [caseId, setCaseId] = React.useState(undefined);
  const [caseList, setCaseList] = React.useState({});
  const [omeDict, setOmeDict] = React.useState({});
  const [pathList, setPathList] = React.useState([]);
  const [pending, setPending] = React.useState(true);
  const [sceneList, setSceneList] = React.useState({});
  const [selCase, setSelCase] = React.useState(null);
  const [selectedFolder, setSelectedFolder] = React.useState(null);

  //renderer stuff
  const [userId, setUserId] = React.useState(null);
  const [activeFileId, setActiveFileId] = React.useState(null);
  const [activeFileName, setActiveFileName] = React.useState(null);
  const [activeFilePath, setActiveFilePath] = React.useState(null);
  const [containerKey, setContainerKey] = React.useState(new Date().getTime());
  const [histogramConfigs, setHistogramConfigs] = React.useState({});
  const [importProgress, setImportProgress] = React.useState(-1);
  const [processingRotation, setProcessingRotation] = React.useState(false);
  const [rendererDict, setRendererDict] = React.useState({});
  const [splitscreenFileIds, setSplitscreenFileIds] = React.useState([]);
  const [chainListFileIds, setChainListFileIds] = React.useState([]);
  const [zoomObjectDict, setZoomObjectDict] = React.useState({});
  const [layoutStateObject, setLayoutStateObject] = React.useState(() => {
    const stored = localStorage.getItem("hsaFileViewerLayoutState");
    return stored
      ? JSON.parse(stored)
      : {
          fileNavButtons: false,
          imageInfo: true,
          macroImage: true,
          miniMap: true,
          scaleBar: true,
          slideLabel: true,
          timeBar: true,
          zoomBar: true,
          zStackBar: true,
        };
  });
  const [commentLayer, setCommentLayer] = React.useState({});
  const [commentRois, setCommentRois] = React.useState([]);
  const [activeTool, setActiveTool] = React.useState(FileViewerTools.None);
  const [tool, setTool] = React.useState(null);
  const [sceneDataList, setSceneDataList] = React.useState([]);
  const [heatMapOpacity, setHeatMapOpacity] = React.useState(50);

  //layout stuff
  const [oldPreviewWidth, setOldPreviewWidth] = React.useState(0);
  const [previewWidth, setPreviewWidth] = React.useState(210);
  const [treeWidth, setTreeWidth] = React.useState(() => {
    const storedWidth = localStorage.getItem("fileViewerSidebarWidth");
    return storedWidth ? parseInt(storedWidth, 10) : 526;
  });

  const [mousePosition, setMousePosition] = React.useState({
    mouseX: 0,
    mouseY: 0,
  });
  const [oldTreeWidth, setOldTreeWidth] = React.useState(0);
  const [isHistogramVisible, setIsHistogramVisible] = React.useState(false);

  const [showAdditionalName, setShowAdditionalName] = React.useState(
    (localStorage.getItem("hsaFileViewerShowFileName") ?? "true") === "true"
  );
  const [showComments, setShowComments] = React.useState(
    (localStorage.getItem("hsaFileViewerShowComments") ?? "true") === "true"
  );

  // foldertree stuff
  const [topLevelEntities, setTopLevelEntities] = React.useState([]);
  const [expanded, setExpanded] = React.useState(
    JSON.parse(localStorage.getItem("hsaExpanded")) || []
  );
  const [openFolder, setOpenFolder] = React.useState([]);

  // sorting stuff
  const MenuType = Object.freeze({
    Preview: "Preview",
    Files: "Files",
    Cases: "Cases",
    Folder: "Folder",
  });

  const [folderSortBy, setFolderSortBy] = React.useState("name");
  const [folderSortReverse, setFolderSortReverse] = React.useState(false);

  const [filePreviewSortBy, setFilePreviewSortBy] = React.useState("name");
  const [filePreviewSortReverse, setFilePreviewSortReverse] =
    React.useState(false);

  const [caseSortBy, setCaseSortBy] = React.useState("name");
  const [caseSortReverse, setCaseSortReverse] = React.useState(false);

  // #region useEffect
  React.useEffect(() => {
    let isMounted = true;
    Backend.getCurrentUser((user) => {
      if (!isMounted) return;
      setUserId(user.id);
    });

    FileFormatUtils.refreshSupportedImageFormats();
    setIsHistogramVisible(
      localStorage.getItem("hsaIsHistogramVisible") === "true"
    );
    const storedActiveFileId = localStorage.getItem("hsaActiveFileId");
    if (storedActiveFileId) {
      setActiveFileId(storedActiveFileId);
    }
    Backend.listCases().then((data) => {
      if (!isMounted) return;
      let currentCase = data.cases.find((obj) => obj.data.Id === caseUrlId);
      if (currentCase === undefined) {
        history.push(`/file_view/`);
      } else {
        setCaseId(currentCase?.data.Id);
      }
      if (data.cases.length === 0) return;
      setCaseList(data);
      setPending(false);
    });
    // init states
    const initActiveFileId = localStorage.getItem("hsaFileViewerActiveFileId");
    const initActiveFilePath = localStorage.getItem(
      "hsaFileViewerActiveFilePath"
    );
    const initSelectedFolder = JSON.parse(
      localStorage.getItem("hsaSelectedFolder")
    );

    // check if path has case id after "file_view/"
    const path = history.location.pathname.split("/");
    const endOfPath = path[path.length - 1];

    if (endOfPath === "file_view" || endOfPath === "") {
      if (initActiveFileId) {
        setActiveFileId(initActiveFileId);
      }
      if (initActiveFilePath) {
        setActiveFilePath(initActiveFilePath);
      }
      if (initSelectedFolder) {
        setSelectedFolder(initSelectedFolder);
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    rendererDictRef.current = rendererDict;
  }, [rendererDict]);

  React.useEffect(() => {
    if (!activeFileId || zoomObjectDict === null) return;
    // setContainerKey(new Date().getTime());
  }, [zoomObjectDict]);

  React.useEffect(() => {
    if (!omeDict || !activeFileId) return;
    const newTool = tools[activeTool];
    if (!newTool) {
      if (tool) {
        tool.exit();
      }
      setTool(null);
      return;
    }
    // Create a fresh comment layer object if it doesn't exist
    const currentCommentLayer = commentLayer[activeFileId] || {
      commentRois: [],
    };

    const toolObject = {
      ome: omeDict[activeFileId],
      commentLayer: currentCommentLayer,
      heatMapOpacity,
      setHeatMapOpacity,
    };
    newTool.setLayer(toolObject);
    setTool(newTool);
  }, [omeDict, activeTool]);

  React.useEffect(() => {
    if (activeFileId === null) return;
    localStorage.setItem("hsaFileViewerActiveFileId", activeFileId);
    setSceneDataList((prevList) =>
      prevList.map((scene) =>
        scene.id === activeFileId ? { ...scene, hasSeen: true } : scene
      )
    );
  }, [activeFileId]);

  React.useEffect(() => {
    if (selectedFolder === null) return;
    localStorage.setItem("hsaSelectedFolder", JSON.stringify(selectedFolder));

    if (selectedFolder !== null && caseId === null) {
      Backend.walkDir(selectedFolder.path, true).then((files) => {
        if (!files) return;

        let filePaths = files
          .filter((file) => file.type === "file")
          .map((file) => {
            return file;
          });
        setPathList(filePaths);
      });
    }
  }, [selectedFolder]);

  React.useEffect(() => {
    function handleMouseMove(e) {
      setMousePosition({ mouseX: e.pageX, mouseY: e.pageY });
    }

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  });

  React.useEffect(() => {
    if (caseId === null || caseId === undefined) return;
    getFilePathsFromCase(caseId);
  }, [caseList]);

  React.useEffect(() => {
    if (pathList.length === 0) return;
    let isMounted = true;
    getScenesForFile(isMounted);
    return () => {
      isMounted = false;
    };
  }, [pathList]);

  React.useEffect(() => {
    if (caseId === undefined) setCaseId(null);
    if (caseId === null) {
      setPathList([]);
      return;
    }
    if (caseId === caseUrlId) return;
    const fileId = new URLSearchParams(history.location.search).get("fileId");
    if (fileId) {
      setActiveFileId(fileId);
    }
    history.replace(`/file_view/${caseId}`);
  }, [caseId]);

  React.useEffect(() => {
    if (activeFileId === null) return;
    Backend.getFileInfo(activeFileId).then((fileInfo) => {
      if (!fileInfo) return;
      setActiveFilePath(fileInfo.relativePath);
      setActiveFileName(fileInfo.fileName);
    });

    localStorage.setItem("hsaActiveFileId", activeFileId);
    const isCommentToolActive = activeTool === FileViewerTools.Comment;
    setActiveTool(FileViewerTools.None);
    Backend.getCommentLayer(activeFileId)
      .then((commentLayer) => {
        if (!commentLayer) return;
        if (!commentLayer.commentRois) {
          commentLayer.commentRois = [];
        }
        setCommentLayer((prevState) => ({
          ...prevState,
          [activeFileId]: commentLayer,
        }));
        if (isCommentToolActive) {
          setActiveTool(FileViewerTools.Comment);
        }
      })
      .catch((error) => {
        console.error("Error fetching comment layer:", error);
      });
  }, [activeFileId]);

  React.useEffect(() => {
    window.zoomToRect = (rect) => {
      let width = rect.right - rect.left;
      let height = rect.bottom - rect.top;
      if (rendererDict[activeFileId]) {
        rendererDict[activeFileId].zoomToRect(
          rect.top - height,
          rect.left - width,
          rect.right + width,
          rect.bottom + height
        );
      }
    };

    return () => {
      window.zoomToRect = undefined; // Cleanup
    };
  }, [activeFileId, rendererDict]); // Add dependencies here

  // load possibly saved histogram config from the backend
  React.useEffect(() => {
    if (!activeFileName || !omeDict[activeFileId]) return;
    const channels = omeDict[activeFileId].channels;

    const histogramConfig = {
      channels: channels.map((c) => ({
        color: c.color || "#ffffff",
        enabled: true,
        gamme: 1,
        max: parseInt(c.high * 255, 10) || 255,
        min: parseInt(c.low * 255, 10) || 0,
        name: c.name || "Channel " + c.id,
        type: c.type || "brightfield",
      })),
      nChannels: channels.length,
    };

    Backend.getHistogramConfig(activeFilePath, histogramConfig).then(
      (response) => {
        if (response?.histogramConfigString) {
          const fileHistogramConfig = JSON.parse(
            response.histogramConfigString
          );
          const newHistogramConfig = {
            ...histogramConfig,
            channels: fileHistogramConfig.channels,
          };
          newHistogramConfig.channels.forEach((channel) => {
            channel.type = channel.type
              ? channel.type
              : newHistogramConfig.channels.length > 1
              ? "fluorescence"
              : "brightfield";
          });

          onChangeChannels(newHistogramConfig);
        }
      }
    );
  }, [omeDict, activeFileName, activeFileId]);

  React.useEffect(() => {
    updateDimensions();
  }, [splitscreenFileIds]);

  // #endregion useEffect

  const onSaveScreenshot = () => {
    let img = rendererDictRef.current[activeFileId].canvas;

    img = img.toDataURL("image/jpeg", 0.9);
    let link = document.createElement("a");
    const fileNameNoExt = activeFileName
      .split(/[/\\]/)
      .pop()
      .replace(/\.[^/.]+$/, "");

    let screenShotName = `${fileNameNoExt}.jpg`;

    link.download = screenShotName;
    link.href = img.replace("image/jpeg", "image/octet-stream");
    link.click();
  };

  const updatePreviewWidth = (newWidth) => {
    setTreeWidth(newWidth);
    localStorage.setItem("fileViewerSidebarWidth", newWidth);
  };

  const getMousePosition = () => {
    return [mousePosition.mouseX, mousePosition.mouseY];
  };

  const toggleHistogram = () => {
    localStorage.setItem("hsaIsHistogramVisible", !isHistogramVisible);
    setIsHistogramVisible(!isHistogramVisible);
  };

  const updateExpanded = (newExpanded) => {
    setExpanded(newExpanded);
    localStorage.setItem("hsaExpanded", JSON.stringify(newExpanded));
  };

  /**
   * Rotates the currently active renderer by the specified degree.
   *
   * The rotation state (`rotState`) is constrained to one of the allowed values: 0, 90, 180, or 270 degrees.
   * If the resulting rotation is not one of these, it is clamped to the nearest allowed value.
   * After updating the rotation state, the renderer is reset.
   *
   * @param {number} deg - The degree to rotate by. Should be either 90 or -90.
   */
  const onRotate = (deg) => {
    // rotState can only be 0, 90, 180, 270; deg can only be 90 or -90
    let r = rendererDict[activeFileId];
    if (!r) return;
    let currentRot = r.state.rotState || 0;
    let newRot = (currentRot + deg) % 360;
    if (newRot < 0) newRot += 360;
    // Clamp to allowed values
    if (![0, 90, 180, 270].includes(newRot)) {
      // Find the closest allowed value
      const allowed = [0, 90, 180, 270];
      newRot = allowed.reduce((prev, curr) =>
        Math.abs(curr - newRot) < Math.abs(prev - newRot) ? curr : prev
      );
    }
    // remember the rotation state in localStorage
    localStorage.setItem("hsaFileViewerRotState" + activeFileId, newRot);
    r.setMountedState({ rotState: newRot }, () => {
      r.loadAllTiles();
    });
    // jump to rotated position
    setZoomObjectDict((prevState) => {
      if (!prevState || !prevState[activeFileId]) return prevState;
      const r = rendererDict[activeFileId];
      const oldZoom = r.props.persistentStorage.load(
        "zoomObjectFV" + r.props.fileId
      );
      // Check if oldZoom exists and has the required properties
      if (
        !oldZoom ||
        typeof oldZoom.zoomBottom !== "number" ||
        typeof oldZoom.zoomTop !== "number" ||
        typeof oldZoom.zoomLeft !== "number" ||
        typeof oldZoom.zoomRight !== "number"
      ) {
        // Optionally, you can log a warning here
        return prevState;
      }
      const newObject = {
        zoomRoi: true,
        zoomLeft: r.vh - oldZoom.zoomBottom,
        zoomRight: r.vh - oldZoom.zoomTop,
        zoomTop: oldZoom.zoomLeft,
        zoomBottom: oldZoom.zoomRight,
      };
      r.props.persistentStorage.save(
        "zoomObjectFV" + r.props.fileId,
        newObject
      );
      return {
        ...prevState,
        [activeFileId]: newObject,
      };
    });

    setContainerKey(new Date().getTime());
  };

  /**
   * Saves the current histogram configuration for the active file type and user.
   */
  const onSave = () => {
    // go through histogramConfigs[activeFileId].channels and add each name to channelNames
    if (histogramConfigs[activeFileId]) {
      const histogramConfig = histogramConfigs[activeFileId];

      Backend.saveHistogramConfig(activeFileName, histogramConfig)
        .then((response) => {
          if (response) {
            window.showSuccessSnackbar(
              "Histogram configuration saved successfully"
            );
          }
        })
        .catch((error) => {
          window.openErrorDialog(
            "Error saving histogram configuration:",
            error.message
          );
        });
    }
    // log commentRois for the activeFileId
    if (commentLayer[activeFileId]) {
      Backend.saveCommentLayer(activeFileId, commentLayer[activeFileId])
        .then((response) => {
          if (response) {
            window.showSuccessSnackbar("Comment layer saved successfully");
          }
        })
        .catch((error) => {
          window.openErrorDialog("Error saving comment layer:", error.message);
        });
    }
  };

  const getFilePathsFromCase = (caseId) => {
    let foundCase = caseList.cases.find((obj) => obj.data.Id === caseId);
    if (foundCase) {
      let pathList = foundCase.filePaths.map((path) => ({
        path,
        type: "file",
      }));
      setPathList(pathList);
      foundCase.data.Files = foundCase.filePaths;
      setSelCase(foundCase.data);
      setTreeWidth(870);
    }
  };

  React.useEffect(() => {
    const getScenesForFile = async (pathList) => {
      let list = await FileFormatUtils.getScenesForFile(pathList);

      // sort by scene.path
      list = FileFormatUtils.sortEntitiesByTypeAndName(
        list,
        filePreviewSortBy,
        false,
        filePreviewSortReverse
      );
      setSceneDataList(list);
    };

    getScenesForFile(pathList.map((pathItem) => pathItem.path));
  }, [pathList, sceneList, filePreviewSortBy, filePreviewSortReverse]);

  React.useEffect(() => {
    if (!selCase) return;
    if (!selCase.Files || selCase.Files.length === 0) return;
  }, [selCase]);

  const getScenesForFile = async (isMounted) => {
    const promises = pathList.map((path) => Backend.getFileIdData(path.path));
    const results = await Promise.all(promises);
    const tempSceneObj = {};
    results.forEach((sceneData, index) => {
      const path = pathList[index].path;
      if (sceneData && sceneData.sceneIds.length > 0) {
        tempSceneObj[path] = sceneData.sceneIds;
      }
    });
    if (isMounted) {
      // setSceneList((prevSceneList) => ({ ...prevSceneList, ...tempSceneObj }));
      setSceneList({ ...tempSceneObj });
    }

    return isMounted;
  };

  const updateDimensions = () => {
    let savedT = 0;
    setContainerKey(new Date().getTime());
    if (rendererDict[activeFileId]?.state) {
      savedT = rendererDict[activeFileId].state.t;
    }
    if (savedT > 0 && rendererDict[activeFileId])
      rendererDict[activeFileId].updateT(savedT);
    handleSavedZoomObjects();
  };

  const onChainSplitscreen = (fileId, e) => {
    if (!chainListFileIds.includes(fileId)) {
      setChainListFileIds([...chainListFileIds, fileId]);
    } else {
      // Remove fileId if present
      setChainListFileIds(chainListFileIds.filter((id) => id !== fileId));
    }

    if (e) {
      e.stopPropagation();
    }
  };

  const handleSavedZoomObjects = () => {
    if (splitscreenFileIds.length === 0) return;
    let newZoomObjectDict = {};
    for (const fileId of splitscreenFileIds) {
      let zoomData = props.persistentStorage.load("zoomObjectFV" + fileId);
      if (zoomData) {
        newZoomObjectDict[fileId] = structuredClone(zoomData);
      } else {
        newZoomObjectDict[fileId] = {
          zoomRoi: false,
          zoomLeft: 0,
          zoomRight: 0,
          zoomTop: 0,
          zoomBottom: 0,
        };
      }
    }
    setZoomObjectDict((prevState) => ({ ...prevState, ...newZoomObjectDict }));
  };

  const emitRendererEvent = (data) => {
    EventBus.emit("chainEvent", data);
  };

  /**
   * Updates the histogram configuration for the active file and updates the renderer if it exists.
   *
   * @param {Object} newHistogramConfig - The new histogram configuration to be applied.
   */
  const onChangeChannels = (newHistogramConfig) => {
    setHistogramConfigs((prevState) => ({
      ...prevState,
      [activeFileId]: newHistogramConfig,
    }));
  };

  React.useEffect(() => {
    if (!activeFileId || !rendererDict[activeFileId]) return;
    const newHistogramConfig = histogramConfigs[activeFileId];
    rendererDict[activeFileId].updateFLChannels(newHistogramConfig);
  }, [histogramConfigs]);

  React.useEffect(() => {
    const newfileIds = [];
    for (const fileId of Object.keys(rendererDict)) {
      // check if the key exists in commentLayer
      if (rendererDict[fileId] && !commentLayer[fileId]) {
        newfileIds.push(fileId);
      }
    }
    if (newfileIds.length === 0) return;
    // for each new fileId, create a new commentLayer with { commentRois: [] } as content
    const newCommentLayer = {};
    newfileIds.forEach((fileId) => {
      newCommentLayer[fileId] = { commentRois: [] };
    });
    setCommentLayer((prevState) => ({
      ...prevState,
      ...newCommentLayer,
    }));
  }, [rendererDict]);

  const resetRenderer = () => {
    setActiveFileId(null);
    setActiveFileName(null);
    setActiveFilePath(null);
    setContainerKey(new Date().getTime());
    setRendererDict({});
    setCommentLayer({});
    setSceneList({});
    setSelectedFolder(null);
    setSplitscreenFileIds([]);
    setZoomObjectDict({});
  };

  const toggleLayoutElement = (elementKey) => {
    setLayoutStateObject((prevState) => {
      const newLayoutStateObject = {
        ...prevState,
        [elementKey]: !prevState[elementKey],
      };
      localStorage.setItem(
        "hsaFileViewerLayoutState",
        JSON.stringify(newLayoutStateObject)
      );
      return newLayoutStateObject;
    });
  };

  /**
   * Toggles the currently active tool in the file viewer sidebar.
   * If the specified tool is already active, it deactivates it by setting to `FileViewerTools.None`.
   * Otherwise, it activates the specified tool.
   *
   * @param {string} toolName - The name of the tool to toggle as active.
   */
  const toggleActiveTool = (toolName) => {
    setActiveTool((prevActiveTool) => {
      if (prevActiveTool === toolName) {
        return FileViewerTools.None;
      } else {
        return toolName;
      }
    });
  };

  /**
   * Handles the import of a rotation object for the currently active file.
   *
   * Initiates the import process, sets the initial import progress, and starts polling
   * the backend for progress updates. When the import reaches 100%, the page reloads.
   * Handles errors by displaying an error dialog and resetting the progress.
   *
   * @param {Object} rotationObject - The rotation data to be imported.
   */
  const onImportRotation = (rotationObject) => {
    setProcessingRotation(true);
    setImportProgress(0);
    Backend.importRotation(activeFileId, rotationObject).then((response) => {
      if (response) {
        Backend.getFileInfo(activeFileId).then((fileInfo) => {
          if (!fileInfo) return;
        });

        const pollInterval = setInterval(() => {
          Backend.getFileInfo(activeFileId)
            .then((fileInfo) => {
              if (fileInfo.progress >= 0) {
                setImportProgress(fileInfo.progress);
              } else {
                setImportProgress(0);
              }
              if (fileInfo && fileInfo.progress === 100) {
                clearInterval(pollInterval);

                window.location.reload(); // Reload page when progress is 100%
                return;
              }
            })
            .catch((error) => {
              console.error("Error fetching file info during import:", error);
              clearInterval(pollInterval);
              window.openErrorDialog("Error fetching file info during import");
              setImportProgress(-1); // Reset progress on error
              setProcessingRotation(false);
            });
        }, 1000);

        // Cleanup interval on component unmount or when activeFileId changes
        return () => clearInterval(pollInterval);
      } else {
        window.openErrorDialog("Error importing rotation");
      }
    });
  };

  // Define all shortcuts and their handlers in one array so we can easily manage them.
  // Each shortcut has a test function to check if the key combination matches
  // and a handler function to execute the corresponding action.
  const shortcutMap = [
    {
      test: (e) => e.shiftKey && e.code === "KeyC",
      handler: () => toggleActiveTool(FileViewerTools.Comment),
    },
    {
      test: (e) => e.shiftKey && e.code === "KeyH",
      handler: () => toggleActiveTool(FileViewerTools.ViewHeatMap),
    },
    {
      test: (e) => e.shiftKey && e.code === "KeyP",
      handler: () => onSaveScreenshot(),
    },
    {
      test: (e) => e.shiftKey && (e.key === "*" || e.key === "+"),
      handler: (fileIds) =>
        emitRendererEvent({
          type: "zoomOneToN",
          value: 1,
          fileIds: fileIds,
        }),
    },
    {
      test: (e) =>
        e.shiftKey && (e.key === "0" || e.key === "=" || e.code === "Numpad0"),
      handler: (fileIds) =>
        emitRendererEvent({ type: "zoomFit", fileIds: fileIds }),
    },
    {
      test: (e) => e.key === "+" && !e.shiftKey,
      handler: (fileIds) =>
        emitRendererEvent({
          type: "zoomDelta",
          value: -1,
          fileIds: fileIds,
        }),
    },
    {
      test: (e) => e.key === "-" && !e.shiftKey,
      handler: (fileIds) =>
        emitRendererEvent({
          type: "zoomDelta",
          value: 1,
          fileIds: fileIds,
        }),
    },
    {
      test: (e) => e.ctrlKey && e.key === "S",
      handler: () => onSave(),
    },
  ];

  // Register the keydown event listener for shortcuts
  // This effect will run once when the component mounts
  // and will clean up the event listener when the component unmounts or dependencies change.
  // useEffect is used, so the handler is using the latest state and props.
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      let fileIds = chainListFileIds;
      if (activeFileId && !fileIds.includes(activeFileId)) {
        fileIds = [activeFileId];
      }
      for (const shortcut of shortcutMap) {
        if (shortcut.test(e)) {
          e.preventDefault(); // Prevent default browser actions if needed
          shortcut.handler(fileIds);
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeFileId,
    chainListFileIds,
    toggleActiveTool,
    onSaveScreenshot,
    emitRendererEvent,
    onSave,
    FileViewerTools.Comment,
    FileViewerTools.ViewHeatMap,
  ]);

  const context = {
    activeFileId,
    activeFileName,
    activeFilePath,
    activeTool,
    caseId,
    caseList,
    caseSortBy,
    caseSortReverse,
    chainListFileIds,
    commentLayer,
    commentRois,
    containerKey,
    emitRendererEvent,
    expanded,
    filePreviewSortBy,
    filePreviewSortReverse,
    folderSortBy,
    folderSortReverse,
    getScenesForFile,
    heatMapOpacity,
    histogramConfigs,
    importProgress,
    isHistogramVisible,
    layoutStateObject,
    MenuType,
    onSaveScreenshot,
    oldPreviewWidth,
    oldTreeWidth,
    omeDict,
    onChainSplitscreen,
    onChangeChannels,
    onImportRotation,
    onRotate,
    onSave,
    openFolder,
    pathList,
    pending,
    previewWidth,
    processingRotation,
    rendererDict,
    resetRenderer,
    sceneDataList,
    sceneList,
    selCase,
    selectedFolder,
    setActiveFileId,
    setActiveFileName,
    setActiveFilePath,
    setCaseId,
    setCaseList,
    setCaseSortBy,
    setCaseSortReverse,
    setChainListFileIds,
    setCommentRois,
    setContainerKey,
    setFilePreviewSortBy,
    setFilePreviewSortReverse,
    setFolderSortBy,
    setFolderSortReverse,
    setHistogramConfigs,
    setImportProgress,
    setOldPreviewWidth,
    setOldTreeWidth,
    setOmeDict,
    setOpenFolder,
    setPathList,
    setPreviewWidth,
    setRendererDict,
    setSceneDataList,
    setSceneList,
    setSelCase,
    setSelectedFolder,
    setShowAdditionalName,
    setShowComments,
    setSplitscreenFileIds,
    setTopLevelEntities,
    setTreeWidth,
    setZoomObjectDict,
    showAdditionalName,
    showComments,
    SortByType,
    splitscreenFileIds,
    toggleActiveTool,
    toggleHistogram,
    toggleLayoutElement,
    tool,
    topLevelEntities,
    treeWidth,
    updateDimensions,
    updateExpanded,
    updatePreviewWidth,
    userId,
    zoomObjectDict,
  };

  return (
    <FileViewerContext.Provider value={context}>
      <Grid container className={classes.root}>
        <FileViewerSplitscreenManager getMousePosition={getMousePosition} />
        <FileViewerSideBar selCase={selCase} />
      </Grid>
    </FileViewerContext.Provider>
  );
}

FileViewer.propTypes = {
  persistentStorage: PropTypes.object,
  tiles: PropTypes.object,
};

export default withPersistentStorage(withTiles(FileViewer));
